import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/hooks/useAuth';

/**
 * Shared behavior for dev-console notification badges (plugin reviews,
 * provider issues): polled every 60s so the badge clears shortly after the
 * condition resolves, with a staleTime so window-focus refetches dedupe
 * against the interval instead of hammering the endpoint on tab switches.
 *
 * `allowedRoles` gates the fetch and defaults to admin-only. It is a
 * parameter rather than a widened constant because this hook is shared:
 * flipping the gate here would silently expose EVERY badge that uses it
 * (currently provider issues as well) to any newly-added role. Each badge
 * opts in to its own audience, and that audience must match the server-side
 * guard on the endpoint it calls — this is a UX gate, not a security
 * boundary.
 */
export function useAdminBadgeCount(
  queryKey: readonly unknown[],
  queryFn: () => Promise<{ count: number }>,
  allowedRoles: readonly string[] = ['admin'],
): number {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isAllowed = allowedRoles.some((role) => roles.includes(role));

  const { data } = useQuery({
    queryKey,
    queryFn,
    enabled: isAllowed,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 45_000,
  });

  return data?.count ?? 0;
}
